/**
 * Icônes de l'interface.
 *
 * Les tracés proviennent directement des `VectorDrawable` de l'application
 * Android (`res/drawable/*.xml`) : le `pathData` d'Android est du chemin SVG
 * valide, il est donc repris sans conversion. Tous sont dessinés dans une
 * grille de 24 × 24.
 *
 * Deux font exception, faute d'équivalent dans l'application Android : `export`
 * — dont le menu de trace laisse l'entrée « Exporter » sans icône au milieu de
 * quatre qui en ont — et `installApp`, qu'une application déjà installée n'a
 * aucune raison de dessiner. Leurs tracés viennent de la bibliothèque Material
 * dont sortent tous les autres.
 */

export type IconName =
  | 'back'
  | 'close'
  | 'delete'
  | 'dragHandle'
  | 'edit'
  | 'export'
  | 'folderOpen'
  | 'fullscreen'
  | 'fullscreenExit'
  | 'guidance'
  | 'info'
  | 'installApp'
  | 'iosShare'
  | 'layers'
  | 'menu'
  | 'moreVert'
  | 'move'
  | 'myLocation'
  | 'route'
  | 'settings'
  | 'visibility'
  | 'zoomIn'
  | 'zoomOut'

const PATHS: Record<IconName, string> = {
  back: 'M20,11H7.83l5.59,-5.59L12,4l-8,8 8,8 1.41,-1.41L7.83,13H20v-2z',
  close:
    'M19,6.41L17.59,5 12,10.59 6.41,5 5,6.41 10.59,12 5,17.59 6.41,19 12,13.41 17.59,19 19,17.59 13.41,12z',
  delete:
    'M6,19c0,1.1 0.9,2 2,2h8c1.1,0 2,-0.9 2,-2V7H6v12zM19,4h-3.5l-1,-1h-5l-1,1H5v2h14V4z',
  dragHandle: 'M20,9H4v2h16V9zM4,15h16v-2H4v2z',
  edit:
    'M3,17.25V21h3.75L17.81,9.94l-3.75,-3.75L3,17.25zM20.71,7.04c0.39,-0.39 0.39,-1.02 0,-1.41l-2.34,-2.34c-0.39,-0.39 -1.02,-0.39 -1.41,0l-1.83,1.83 3.75,3.75 1.83,-1.83z',
  // Flèche descendant dans un bac : le geste aboutit toujours à un fichier,
  // que l'utilisateur l'enregistre ou le partage.
  export:
    'M19,12v7H5v-7H3v7c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2v-7h-2zm-6,0.67l2.59,-2.58L17,11.5l-5,5 -5,-5 1.41,-1.41L11,12.67V3h2v9.67z',
  folderOpen:
    'M20,6h-8l-2,-2H4C2.9,4 2,4.9 2,6v12c0,1.1 0.9,2 2,2h16c1.1,0 2,-0.9 2,-2V8C22,6.9 21.1,6 20,6zM20,18H4V8h16V18z',
  fullscreen:
    'M7,14H5v5h5v-2H7v-3zM5,10h2V7h3V5H5v5zM17,17h-3v2h5v-5h-2v3zM14,5v2h3v3h2V5h-5z',
  fullscreenExit:
    'M5,16h3v3h2v-5H5v2zM8,8H5v2h5V5H8v3zM14,19h2v-3h3v-2h-5v5zM16,8V5h-2v5h5V8h-3z',
  guidance: 'M12,2L4.5,20.29l0.71,0.71L12,18l6.79,3 0.71,-0.71z',
  info:
    'M12,2C6.48,2 2,6.48 2,12s4.48,10 10,10 10,-4.48 10,-10S17.52,2 12,2zM13,17h-2v-6h2v6zM13,9h-2V7h2v2z',
  // Un téléphone recevant une flèche, et non le glyphe de partage : ici la
  // boîte du navigateur s'ouvre vraiment, et ce qu'elle pose est une
  // application dans le tiroir de l'appareil.
  installApp:
    'M17,1.01L7,1C5.9,1 5,1.9 5,3v18c0,1.1 0.9,2 2,2h10c1.1,0 2,-0.9 2,-2V3c0,-1.1 -0.9,-1.99 -2,-1.99zM17,19H7V5h10v14zm-1,-6h-3V8h-2v5H8l4,4 4,-4z',
  // Le glyphe de partage d'iOS, et non une icône d'installation : c'est
  // exactement le bouton que l'utilisateur devra reconnaître dans Safari.
  iosShare:
    'M16,5l-1.42,1.42 -1.59,-1.59V16h-1.98V4.83L9.42,6.42 8,5l4,-4 4,4zM20,10v11c0,1.1 -0.9,2 -2,2H6c-1.11,0 -2,-0.9 -2,-2V10c0,-1.11 0.89,-2 2,-2h3v2H6v11h12V10h-3V8h3c1.1,0 2,0.89 2,2z',
  layers:
    'M11.99,18.54l-7.37,-5.73L3,14.07l9,7 9,-7 -1.63,-1.27 -7.38,5.74zM12,16l7.36,-5.73L21,9l-9,-7 -9,7 1.63,1.27L12,16z',
  menu: 'M3,18h18v-2H3v2zM3,13h18v-2H3v2zM3,6v2h18V6H3z',
  moreVert:
    'M12,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,10c-1.1,0 -2,0.9 -2,2s0.9,2 2,2 2,-0.9 2,-2 -0.9,-2 -2,-2zM12,16c-1.1,0 -2,0.9 -2,2s0.9,2 2,2 2,-0.9 2,-2 -0.9,-2 -2,-2z',
  move:
    'M10,9h4L14,6h3l-5,-5 -5,5h3v3zM9,10L6,10L6,7l-5,5 5,5v-3h3v-4zM23,12l-5,-5v3h-3v4h3v3l5,-5zM14,15h-4v3L7,18l5,5 5,-5h-3v-3z',
  myLocation:
    'M12,8c-2.21,0 -4,1.79 -4,4s1.79,4 4,4 4,-1.79 4,-4 -1.79,-4 -4,-4zM20.94,11c-0.46,-4.17 -3.77,-7.48 -7.94,-7.94V1h-2v2.06C6.83,3.52 3.52,6.83 3.06,11H1v2h2.06c0.46,4.17 3.77,7.48 7.94,7.94V23h2v-2.06c4.17,-0.46 7.48,-3.77 7.94,-7.94H23v-2h-2.06zM12,19c-3.87,0 -7,-3.13 -7,-7s3.13,-7 7,-7 7,3.13 7,7 -3.13,7 -7,7z',
  route:
    'M19,15.18V7c0,-2.21 -1.79,-4 -4,-4s-4,1.79 -4,4v10c0,1.1 -0.9,2 -2,2s-2,-0.9 -2,-2V8.82C8.16,8.4 9,7.3 9,6c0,-1.66 -1.34,-3 -3,-3S3,4.34 3,6c0,1.3 0.84,2.4 2,2.82V17c0,2.21 1.79,4 4,4s4,-1.79 4,-4V7c0,-1.1 0.9,-2 2,-2s2,0.9 2,2v8.18c-1.16,0.42 -2,1.52 -2,2.82c0,1.66 1.34,3 3,3s3,-1.34 3,-3C21,16.7 20.16,15.6 19,15.18zM6,7c-0.55,0 -1,-0.45 -1,-1s0.45,-1 1,-1s1,0.45 1,1S6.55,7 6,7zM18,19c-0.55,0 -1,-0.45 -1,-1s0.45,-1 1,-1s1,0.45 1,1S18.55,19 18,19z',
  settings:
    'M19.14,12.94c0.04,-0.3 0.06,-0.61 0.06,-0.94c0,-0.32 -0.02,-0.64 -0.07,-0.94l2.03,-1.58c0.18,-0.14 0.23,-0.41 0.12,-0.61l-1.92,-3.32c-0.12,-0.22 -0.37,-0.29 -0.59,-0.22l-2.39,0.96c-0.5,-0.38 -1.03,-0.7 -1.62,-0.94L14.4,2.81c-0.04,-0.24 -0.24,-0.41 -0.48,-0.41h-3.84c-0.24,0 -0.43,0.17 -0.47,0.41L9.25,5.35C8.66,5.59 8.12,5.92 7.63,6.29L5.24,5.33c-0.22,-0.08 -0.47,0 -0.59,0.22L2.74,8.87C2.62,9.08 2.66,9.34 2.86,9.48l2.03,1.58C4.84,11.36 4.8,11.69 4.8,12s0.02,0.64 0.07,0.94l-2.03,1.58c-0.18,0.14 -0.23,0.41 -0.12,0.61l1.92,3.32c0.12,0.22 0.37,0.29 0.59,0.22l2.39,-0.96c0.5,0.38 1.03,0.7 1.62,0.94l0.36,2.54c0.05,0.24 0.24,0.41 0.48,0.41h3.84c0.24,0 0.44,-0.17 0.47,-0.41l0.36,-2.54c0.59,-0.24 1.13,-0.56 1.62,-0.94l2.39,0.96c0.22,0.08 0.47,0 0.59,-0.22l1.92,-3.32c0.12,-0.22 0.07,-0.47 -0.12,-0.61L19.14,12.94zM12,15.6c-1.98,0 -3.6,-1.62 -3.6,-3.6s1.62,-3.6 3.6,-3.6s3.6,1.62 3.6,3.6S13.98,15.6 12,15.6z',
  visibility:
    'M12,4.5C7,4.5 2.73,7.61 1,12c1.73,4.39 6,7.5 11,7.5s9.27,-3.11 11,-7.5c-1.73,-4.39 -6,-7.5 -11,-7.5zM12,17c-2.76,0 -5,-2.24 -5,-5s2.24,-5 5,-5 5,2.24 5,5 -2.24,5 -5,5zM12,9c-1.66,0 -3,1.34 -3,3s1.34,3 3,3 3,-1.34 3,-3 -1.34,-3 -3,-3z',
  zoomIn: 'M19,13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  zoomOut: 'M19,13H5v-2h14v2z',
}

/**
 * Crée une icône SVG.
 *
 * `currentColor` est utilisé comme remplissage : la teinte suit celle du
 * conteneur, comme le faisait le `tint` des `VectorDrawable`.
 */
export function createIcon(name: IconName, size = 24): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('width', String(size))
  svg.setAttribute('height', String(size))
  svg.setAttribute('fill', 'currentColor')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', PATHS[name])
  svg.append(path)

  return svg
}

/** Même icône, sous forme de balisage — pratique pour les gabarits. */
export function iconMarkup(name: IconName, size = 24): string {
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="currentColor"` +
    ` aria-hidden="true" focusable="false"><path d="${PATHS[name]}"/></svg>`
  )
}

/**
 * Flèche de position, reprise de `ic_navigation_arrow_*.xml`.
 *
 * Cinq couches superposées : disque blanc, anneau bleu, disque blanc
 * intérieur, chevron bleu, puis contour du chevron.
 */
export function navigationArrowMarkup(size = 32): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true">
  <circle cx="12" cy="12" r="11" fill="#FFFFFF"/>
  <circle cx="12" cy="12" r="11" fill="none" stroke="#4285F4" stroke-width="1.5"/>
  <circle cx="12" cy="12" r="9.5" fill="#FFFFFF"/>
  <path d="M12,4L6,18L12,14L18,18Z" fill="#4285F4"/>
  <path d="M12,4L6,18L12,14L18,18Z" fill="none" stroke="#1A73E8" stroke-width="0.5"/>
</svg>`
}

/**
 * Point de position simple, affiché hors suivi.
 *
 * Reprend le repère standard des cartes — disque plein cerclé de blanc — que
 * `RenderMode.NORMAL` dessine côté Android tant que le suivi n'est pas actif.
 * La flèche, elle, ne dit quelque chose qu'en mouvement : à l'arrêt, elle
 * pointerait dans une direction arbitraire.
 *
 * Les proportions sont celles du SDK MapLibre Android, que `RenderMode.NORMAL`
 * utilise telles quelles : 22 dp de cerclage blanc
 * (`maplibre_user_stroke_icon`) pour 14 dp de disque coloré
 * (`maplibre_user_icon`), soit un peu moins des deux tiers. Le disque occupait
 * ici les quatre cinquièmes, ce qui donnait un point sensiblement plus lourd
 * que sur Android à taille égale. Le bleu, lui, reste celui de la flèche :
 * deux teintes pour un même repère se remarqueraient à la bascule.
 */
export function positionDotMarkup(size = 22): string {
  return `<svg viewBox="0 0 22 22" width="${size}" height="${size}" aria-hidden="true">
  <circle cx="11" cy="11" r="11" fill="#FFFFFF"/>
  <circle cx="11" cy="11" r="7" fill="#4285F4"/>
</svg>`
}
